"use client";

import Link from "next/link";
import type {
  CNestedCatalogsContent,
  CNestedCatalogItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cNestedCatalogsTranslations } from "./CNestedCatalogs.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CNestedCatalogs.module.css";

/**
 * CNestedCatalogs - visitor view komponentu `c_nested_catalogs`.
 *
 * **Data source:** `C_NESTED_CATALOGS_CONTROLLER::getApiContent()` zwraca:
 *   ```
 *   {
 *     id, symbol,
 *     items: [{ id, name, href, icon }, ...]
 *   }
 *   ```
 *
 * Renderuje siatkę kafelków nawigacyjnych - katalogi potomne bieżącego
 * katalogu. Odpowiednik legacy Smarty templatu
 * `c_nested_catalogs_default_modern.imp` (siatka 4-kolumnowa z animowanym
 * hoverem, ikona Nucleo opcjonalna).
 */
export function CNestedCatalogs({ content }: CNestedCatalogsProps) {
  const t = useT(cNestedCatalogsTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  const items: CNestedCatalogItem[] = Array.isArray(content.items)
    ? content.items
    : [];

  if (items.length === 0) {
    return <div className={styles.empty}>{t.emptyList}</div>;
  }

  return (
    <div className={styles.wrap}>
      <ul className={styles.list}>
        {items.map((item) => (
          <CatalogTile key={item.id} item={item} />
        ))}
      </ul>
    </div>
  );
}

export type CNestedCatalogsProps = {
  content: CNestedCatalogsContent;
};

function CatalogTile({ item }: { item: CNestedCatalogItem }) {
  const href = extractInternalHref(item.href);

  const inner = (
    <>
      {item.icon ? (
        <i className={`nc-icon-outline ${item.icon}`} aria-hidden="true" />
      ) : null}
      <span className={styles.label}>{item.name}</span>
    </>
  );

  return (
    <li className={styles.element}>
      <div className={styles.elementInner}>
        {href !== null ? (
          <Link href={href} className={styles.anchor}>
            {inner}
          </Link>
        ) : (
          <a href={item.href} className={styles.anchor}>
            {inner}
          </a>
        )}
      </div>
    </li>
  );
}
